import { FC, useCallback, useEffect } from "react";
import ReactGA from "react-ga4";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { useError } from "utils/hooks";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, useTheme } from "@mui/material";

import SubmitButton from "../Submit/SubmitButton.tsx";

import HallsGroup from "./components/HallsGroup.tsx";
import { HallsFormScheme, hallsScheme } from "./validation.ts";

type HallsProps = {
  defaultValues: HallsFormScheme;
};

const HallsForm: FC<HallsProps> = ({ defaultValues }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    watch,
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm<HallsFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(hallsScheme),
    defaultValues,
  });

  // TODO: add error handling
  useError(errors);

  const onSubmit: SubmitHandler<HallsFormScheme> = useCallback(async () => {
    if (isValid) {
      ReactGA.event({
        category: "booking_creation",
        action: "hall_selected",
        label: searchParams.toString(),
      });
      navigate(`${Routes.BookingCreationTimeSelection}?${searchParams.toString()}`);
    }
  }, [isValid, navigate, searchParams]);

  const selectedHall = watch("halls");

  useEffect(() => {
    if (selectedHall) {
      setSearchParams((prev) => {
        prev.set("h", selectedHall);

        return prev;
      });
    }
  }, [setSearchParams, selectedHall]);

  return (
    <Box
      component="form"
      autoComplete="off"
      id="halls-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      mb={theme.spacing(12)}
    >
      <HallsGroup control={control} name="halls" />
      {isValid && <SubmitButton form="halls-form" disabled={!isValid} />}
    </Box>
  );
};

export default HallsForm;
